import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileText,
  Gavel,
  Home,
  Link2,
  Plus,
  Printer,
  Save,
  Sigma,
  Trash2,
} from "lucide-react";
import { debtorFullName, filingDebtors } from "../utils/debtors";
import {
  claimLiabilitySummary,
  claimReconciliationSummary,
  claimUnsecuredAmount,
  proofOfClaimsReportFilename,
  scheduledClaimDefaults,
  sortCreditorsByClaimAmount,
} from "../utils/proofOfClaims";

const API_URL = process.env.REACT_APP_API_URL;
const EDITABLE_FIELDS = [
  "claim_number",
  "creditor_name",
  "creditor_address",
  "amended",
  "filed_date",
  "status",
  "liability",
  "claim_classification",
  "claim_type",
  "amount_claimed",
  "secured_amount",
  "priority_amount",
  "unsecured_amount",
  "administrative_amount",
  "arrearage",
  "interest_rate",
  "collateral_value",
  "collateral_description",
  "servicer_name",
  "matched_creditor",
  "include_in_plan",
  "notes",
];
const NUMERIC_FIELDS = new Set([
  "amount_claimed",
  "secured_amount",
  "priority_amount",
  "unsecured_amount",
  "administrative_amount",
  "arrearage",
  "interest_rate",
  "collateral_value",
]);

const money = (value) => (Number(value) || 0).toLocaleString(undefined, {
  style: "currency",
  currency: "USD",
});

const localDate = (value) => {
  if (!value) return "Not set";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
};

const responseError = async (response, fallback) => {
  const payload = await response.json().catch(() => ({}));
  const first = Object.values(payload || {})[0];
  if (Array.isArray(first)) return first[0] || fallback;
  return payload.error || payload.detail || first || fallback;
};

const fieldClass = "w-full rounded border border-gray-300 px-2 py-1.5 text-sm";

const draftPayload = (draft) => {
  const payload = {};
  EDITABLE_FIELDS.forEach((field) => {
    let value = draft[field];
    if (NUMERIC_FIELDS.has(field) && value === "") value = null;
    if (field === "matched_creditor" && value === "") value = null;
    payload[field] = value;
  });
  return payload;
};

export function ClaimCourtDocuments({ caseId, claim, authHeaders, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const submitting = useRef(false);
  const mounted = useRef(true);
  const mutationVersion = useRef(0);
  const pollingWindow = useRef({ scope: "", deadline: 0 });
  const update = useRef(onChange);
  update.current = onChange;
  const download = claim.pdf_download;
  const active = ["pending", "running"].includes(download?.status);
  const canManage = claim.can_manage_documents === true;
  const documents = (claim.documents || []).filter((document) => document.signed_url);
  const retryAt = Date.parse(download?.next_attempt_at);
  const pollDelay = download?.status === "pending" && !download.manual_retry_queued && Number.isFinite(retryAt)
    ? Math.min(30000, Math.max(4000, retryAt - Date.now())) : 4000;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const scope = `${caseId}:${claim.id}`;
    if (pollingWindow.current.scope !== scope) pollingWindow.current = { scope, deadline: 0 };
    if (!canManage || !active) {
      pollingWindow.current.deadline = 0;
      return undefined;
    }
    if (!pollingWindow.current.deadline) pollingWindow.current.deadline = Date.now() + 5 * 60 * 1000;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      if (Date.now() >= pollingWindow.current.deadline) {
        setNotice("Retrieval continues in the background. Reopen this claim to check its latest status.");
        return;
      }
      // Do not allow a status GET started during a queue POST to undo the
      // POST's newer response when those two requests arrive out of order.
      if (submitting.current) {
        timer = window.setTimeout(poll, pollDelay);
        return;
      }
      const version = mutationVersion.current;
      try {
        const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/${claim.id}/`, {
          headers: authHeaders, signal: controller.signal,
        });
        if (!response.ok) throw new Error("Could not refresh the claim PDF status.");
        const updated = await response.json();
        if (!controller.signal.aborted && mutationVersion.current === version && String(updated.id) === String(claim.id)) {
          update.current(updated);
          setNotice("");
        }
      } catch (requestError) {
        if (!controller.signal.aborted) setNotice("The download is still queued. Its status could not be refreshed; checking again shortly.");
      }
      if (!controller.signal.aborted && Date.now() < pollingWindow.current.deadline) {
        timer = window.setTimeout(poll, pollDelay);
      } else if (!controller.signal.aborted) {
        setNotice("Retrieval continues in the background. Reopen this claim to check its latest status.");
      }
    };
    timer = window.setTimeout(poll, pollDelay);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [caseId, claim.id, active, canManage, authHeaders, pollDelay]);

  const retrieve = async () => {
    if (submitting.current) return;
    submitting.current = true;
    mutationVersion.current += 1;
    pollingWindow.current.deadline = Date.now() + 5 * 60 * 1000;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/${claim.id}/retry-pdf/`, {
        method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" }, body: "{}",
      });
      const payload = await response.json().catch(() => ({}));
      if ((!response.ok && response.status !== 409) || String(payload.claim?.id) !== String(claim.id)) {
        throw new Error(payload.detail || "Could not queue the claim PDFs and attachments.");
      }
      if (!mounted.current) return;
      mutationVersion.current += 1;
      update.current(payload.claim);
      setNotice(payload.detail || "Claim PDFs and attachments queued for retrieval.");
    } catch (requestError) {
      if (mounted.current) setError(requestError.message || "Could not queue the claim PDFs and attachments.");
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const statusText = {
    pending: "Claim PDFs and attachments are queued for retrieval.",
    running: "Retrieving the claim PDF and its attachments…",
    succeeded: "Claim PDF retrieval is complete.",
    blocked: "Some court PDFs need attention. Saved PDFs remain available below.",
    failed: "The court PDFs could not all be retrieved. Saved PDFs remain available below.",
  }[download?.status];

  return (
    <div className="mt-4 space-y-2 rounded border bg-white p-3">
      <h4 className="text-sm font-semibold">Claim PDFs and attachments</h4>
      {statusText && <p role="status" className="text-sm text-slate-700">{statusText}</p>}
      {canManage && ["blocked", "failed"].includes(download?.status) && download.last_error && (
        <p className="text-sm text-amber-800">{download.last_error}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {documents.map((document) => (
          <a key={document.id} href={document.signed_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded border px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50">
            <FileText className="h-4 w-4" /> {document.description || "View claim PDF"}
          </a>
        ))}
      </div>
      {!documents.length && <p className="text-sm text-slate-500">No claim PDFs have been saved yet.</p>}
      {canManage && (
        <button type="button" onClick={retrieve} disabled={busy || !claim.can_retry_pdf} className="rounded border border-indigo-300 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
          {busy ? "Queuing…" : active && !claim.can_retry_pdf ? "PDF retrieval in progress" : "Retrieve PDFs and attachments"}
        </button>
      )}
      {canManage && !claim.can_retry_pdf && !active && <p className="text-xs text-slate-500">No saved court download link is available for this claim.</p>}
      {notice && <p role="status" className="text-sm text-slate-600">{notice}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </div>
  );
}

export default function ProofOfClaimSection({
  caseId,
  caseData,
  creditors = [],
  onCreditorCreated,
}) {
  const token = localStorage.getItem("accessToken");
  const [claims, setClaims] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openClaimId, setOpenClaimId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadClaims = async () => {
    const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/`, {
      headers: authHeaders,
    });
    if (!response.ok) throw new Error(await responseError(response, "Unable to load proofs of claim."));
    const payload = await response.json();
    setClaims(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    if (!caseId) return;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [claimsResponse, assetsResponse] = await Promise.all([
          fetch(`${API_URL}/case/${caseId}/proof-of-claim/`, { headers: authHeaders }),
          fetch(`${API_URL}/case/${caseId}/creditors/linkable-assets/`, { headers: authHeaders }),
        ]);
        if (!claimsResponse.ok) {
          throw new Error(await responseError(claimsResponse, "Unable to load proofs of claim."));
        }
        const claimsPayload = await claimsResponse.json();
        setClaims(Array.isArray(claimsPayload) ? claimsPayload : []);
        if (assetsResponse.ok) {
          const assetsPayload = await assetsResponse.json();
          setAssets(Array.isArray(assetsPayload.assets) ? assetsPayload.assets : []);
        }
      } catch (requestError) {
        setError(requestError.message || "Unable to load proofs of claim.");
      } finally {
        setLoading(false);
      }
    })();
  }, [caseId, authHeaders]);

  const debtors = filingDebtors(caseData?.debtors);
  const debtorOne = debtorFullName(debtors[0]) || "Debtor 1";
  const debtorTwo = debtorFullName(debtors[1]) || "Debtor 2";
  const summary = useMemo(() => claimReconciliationSummary(claims), [claims]);
  const liabilitySummary = useMemo(() => claimLiabilitySummary(claims), [claims]);
  const rankedCreditors = useMemo(
    () => sortCreditorsByClaimAmount(creditors, draft?.amount_claimed),
    [creditors, draft?.amount_claimed],
  );
  const liabilityLabels = {
    debtor1: `${debtorOne} (Debtor 1)`,
    debtor2: `${debtorTwo} (Debtor 2)`,
    joint: "Joint — Debtor 1 and Debtor 2",
    debtor1_third: `${debtorOne} and third party`,
    debtor2_third: `${debtorTwo} and third party`,
    third: "Third party only",
    unknown: "Not reviewed / unassigned",
  };
  const visibleLiabilityRows = liabilitySummary.filter((row) => {
    if (row.claimCount > 0) return true;
    if (row.key === "debtor1" || row.key === "unknown") return true;
    return !!debtors[1] && ["debtor2", "joint"].includes(row.key);
  });
  const includedClaims = claims.filter(
    (claim) => claim.include_in_plan !== false && ["active", "transferred"].includes(claim.status || "active"),
  );
  const totals = includedClaims.reduce((result, claim) => ({
    claimed: result.claimed + (Number(claim.amount_claimed) || 0),
    secured: result.secured + (Number(claim.secured_amount) || 0),
    priority: result.priority + (Number(claim.priority_amount) || 0),
    unsecured: result.unsecured + claimUnsecuredAmount(claim),
  }), { claimed: 0, secured: 0, priority: 0, unsecured: 0 });

  const toggleClaim = (claim) => {
    if (openClaimId === claim.id) {
      setOpenClaimId(null);
      setDraft(null);
      return;
    }
    setOpenClaimId(claim.id);
    setDraft({ ...claim, matched_creditor: claim.matched_creditor || "" });
    setSelectedAsset("");
  };

  const updateClaimDocuments = (updated) => {
    const documentFields = {
      documents: updated.documents,
      pdf_download: updated.pdf_download,
      can_manage_documents: updated.can_manage_documents,
      can_retry_pdf: updated.can_retry_pdf,
    };
    setClaims((current) => current.map((claim) => String(claim.id) === String(updated.id) ? { ...claim, ...documentFields } : claim));
    // A status poll must never overwrite unsaved claim-review edits.
    setDraft((current) => current && String(current.id) === String(updated.id) ? { ...current, ...documentFields } : current);
  };

  const selectMatchedCreditor = (creditorId) => {
    const creditor = creditors.find((item) => String(item.id) === String(creditorId));
    setDraft((current) => ({
      ...current,
      matched_creditor: creditorId,
      ...(creditor ? scheduledClaimDefaults(creditor) : {}),
    }));
  };

  const createManualClaim = async () => {
    const nextNumber = Math.max(0, ...claims.map((claim) => Number(claim.claim_number) || 0)) + 1;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          claim_number: nextNumber,
          creditor_name: "New filed claim",
          creditor_address: "",
          email_body: "",
          status: "active",
          liability: "unknown",
          claim_classification: "unsecured",
          claim_type: "other",
          include_in_plan: true,
        }),
      });
      if (!response.ok) throw new Error(await responseError(response, "Unable to add the claim."));
      const created = await response.json();
      setClaims((current) => [...current, created].sort((a, b) => a.claim_number - b.claim_number));
      setOpenClaimId(created.id);
      setDraft({ ...created, matched_creditor: "" });
    } catch (requestError) {
      setError(requestError.message || "Unable to add the claim.");
    } finally {
      setSaving(false);
    }
  };

  const saveClaim = async () => {
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/${draft.id}/`, {
        method: "PATCH",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(draftPayload(draft)),
      });
      if (!response.ok) throw new Error(await responseError(response, "Unable to save the claim."));
      const updated = await response.json();
      setClaims((current) => current.map((claim) => claim.id === updated.id ? updated : claim));
      setDraft({ ...updated, matched_creditor: updated.matched_creditor || "" });
      if (updated.matched_creditor && onCreditorCreated) {
        await onCreditorCreated();
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to save the claim.");
    } finally {
      setSaving(false);
    }
  };

  const linkAsset = async () => {
    if (!draft || !selectedAsset) return;
    const [assetType, objectId] = selectedAsset.split("::");
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/${draft.id}/asset-links/`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ asset_type: assetType, object_id: objectId }),
      });
      if (!response.ok) throw new Error(await responseError(response, "Unable to link collateral."));
      const updated = await response.json();
      setClaims((current) => current.map((claim) => claim.id === updated.id ? updated : claim));
      setDraft({ ...updated, matched_creditor: updated.matched_creditor || "" });
      setSelectedAsset("");
    } catch (requestError) {
      setError(requestError.message || "Unable to link collateral.");
    } finally {
      setSaving(false);
    }
  };

  const unlinkAsset = async (linkId) => {
    if (!draft) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/proof-of-claim/${draft.id}/asset-links/${linkId}/`,
        { method: "DELETE", headers: authHeaders },
      );
      if (!response.ok) throw new Error(await responseError(response, "Unable to remove collateral."));
      await loadClaims();
      setDraft((current) => ({
        ...current,
        asset_links: (current.asset_links || []).filter((link) => link.id !== linkId),
      }));
    } catch (requestError) {
      setError(requestError.message || "Unable to remove collateral.");
    } finally {
      setSaving(false);
    }
  };

  const createScheduledCreditor = async () => {
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      const saveResponse = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/${draft.id}/`, {
        method: "PATCH",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(draftPayload(draft)),
      });
      if (!saveResponse.ok) {
        throw new Error(await responseError(saveResponse, "Save the claim review before creating its creditor."));
      }
      const response = await fetch(
        `${API_URL}/case/${caseId}/proof-of-claim/${draft.id}/create-creditor/`,
        { method: "POST", headers: authHeaders },
      );
      if (!response.ok) throw new Error(await responseError(response, "Unable to create a scheduled creditor."));
      await loadClaims();
      if (onCreditorCreated) await onCreditorCreated();
      setOpenClaimId(null);
      setDraft(null);
    } catch (requestError) {
      setError(requestError.message || "Unable to create a scheduled creditor.");
    } finally {
      setSaving(false);
    }
  };

  const downloadLiabilityReport = async () => {
    setDownloadingReport(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/proof-of-claim/report/`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error(await responseError(response, "Unable to create the claims report."));
      }
      // Build this locally because cross-origin responses do not always expose
      // Content-Disposition to browser JavaScript, even when the server sends it.
      const filename = proofOfClaimsReportFilename(caseData, debtors[0]);
      const objectUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      setError(requestError.message || "Unable to create the claims report.");
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) return <p className="mt-8 text-sm text-gray-600">Loading proof-of-claim entries…</p>;

  return (
    <section className="mt-8 border-t pt-8">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-indigo-700">Proof of Claims</h2>
          <p className="text-sm text-gray-600">Claims deadline: {localDate(caseData?.proof_of_claims_deadline)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadLiabilityReport}
            disabled={downloadingReport}
            className="inline-flex items-center gap-2 rounded border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4" /> {downloadingReport ? "Creating PDF…" : "Print claims PDF"}
          </button>
          <button
            type="button"
            onClick={createManualClaim}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Add filed claim
          </button>
        </div>
      </div>

      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{String(error)}</div>}

      <div className="mb-5 grid grid-cols-2 gap-3 text-center md:grid-cols-4">
        {[
          [Sigma, "Total filed", totals.claimed, "text-gray-800"],
          [Home, "Secured", totals.secured, "text-indigo-600"],
          [Gavel, "Priority", totals.priority, "text-red-600"],
          [CreditCard, "Unsecured", totals.unsecured, "text-green-600"],
        ].map(([Icon, label, value, color]) => (
          <div key={label} className="rounded border bg-white p-3 shadow-sm">
            <Icon className={`mx-auto mb-1 h-5 w-5 ${color}`} />
            <div className="text-xs font-semibold text-gray-600">{label}</div>
            <div className="font-semibold">{money(value)}</div>
          </div>
        ))}
      </div>

      {(summary.unmatchedCount > 0 || summary.warningCount > 0) && (
        <div className="mb-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{summary.unmatchedCount} active claim{summary.unmatchedCount === 1 ? " is" : "s are"} unmatched and {summary.warningCount} review warning{summary.warningCount === 1 ? " remains" : "s remain"}.</span>
        </div>
      )}

      <div className="mb-5 overflow-hidden rounded-lg border border-indigo-100 bg-white shadow-sm">
        <div className="border-b border-indigo-100 bg-indigo-50 px-4 py-3">
          <h3 className="font-semibold text-indigo-900">Liability summary</h3>
          <p className="text-xs text-indigo-700">Active claims used in plan calculations, grouped by who is liable.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-semibold">Liability</th>
                <th className="px-4 py-2 text-right font-semibold">Claims</th>
                <th className="px-4 py-2 text-right font-semibold">Secured</th>
                <th className="px-4 py-2 text-right font-semibold">Priority</th>
                <th className="px-4 py-2 text-right font-semibold">Unsecured</th>
                <th className="px-4 py-2 text-right font-semibold">Total</th>
                <th className="px-4 py-2 text-right font-semibold">Unmatched</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleLiabilityRows.map((row) => (
                <tr key={row.key} className={row.key === "unknown" && row.claimCount ? "bg-amber-50" : ""}>
                  <td className="px-4 py-2 font-medium text-gray-800">{liabilityLabels[row.key]}</td>
                  <td className="px-4 py-2 text-right">{row.claimCount}</td>
                  <td className="px-4 py-2 text-right">{money(row.securedAmount)}</td>
                  <td className="px-4 py-2 text-right">{money(row.priorityAmount)}</td>
                  <td className="px-4 py-2 text-right">{money(row.unsecuredAmount)}</td>
                  <td className="px-4 py-2 text-right font-medium">{money(row.amount)}</td>
                  <td className={`px-4 py-2 text-right ${row.unmatchedCount ? "font-semibold text-amber-700" : ""}`}>{row.unmatchedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!claims.length ? (
        <p className="rounded border bg-gray-50 p-4 text-sm text-gray-600">No proof-of-claim entries have been received or entered.</p>
      ) : (
        <div className="space-y-3">
          {claims.map((claim) => {
            const open = openClaimId === claim.id;
            const warnings = claim.reconciliation_warnings || [];
            return (
              <article key={claim.id} className={`overflow-hidden rounded-lg border ${warnings.length ? "border-amber-300" : "border-gray-200"}`}>
                <button
                  type="button"
                  onClick={() => toggleClaim(claim)}
                  className="flex w-full items-center justify-between gap-4 bg-white px-4 py-3 text-left hover:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">Claim #{claim.claim_number} — {claim.creditor_name}</span>
                      {claim.amended && <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">Amended</span>}
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs capitalize">{claim.claim_classification?.replaceAll("_", " ")}</span>
                      <span className={`rounded px-2 py-0.5 text-xs ${claim.liability === "unknown" ? "bg-amber-100 text-amber-900" : "bg-blue-100 text-blue-800"}`}>
                        {liabilityLabels[claim.liability] || liabilityLabels.unknown}
                      </span>
                      {!claim.matched_creditor && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">Unmatched</span>}
                      {!claim.include_in_plan && <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">Not used in plan</span>}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      Claimed {money(claim.amount_claimed)} • Filed {localDate(claim.filed_date)} • Secured {money(claim.secured_amount)} • Priority {money(claim.priority_amount)} • Unsecured {money(claimUnsecuredAmount(claim))}
                    </div>
                    {claim.matched_creditor_details && (
                      <div className="mt-1 text-xs text-gray-500">
                        Matched to {claim.matched_creditor_details.account_name} • Scheduled {money(claim.matched_creditor_details.balance)} • Variance {money((Number(claim.amount_claimed) || 0) - (Number(claim.matched_creditor_details.balance) || 0))}
                      </div>
                    )}
                  </div>
                  {open ? <ChevronUp className="h-5 w-5 shrink-0" /> : <ChevronDown className="h-5 w-5 shrink-0" />}
                </button>

                {open && draft && (
                  <div className="border-t bg-gray-50 p-4">
                    {warnings.length > 0 && (
                      <ul className="mb-4 list-disc rounded border border-amber-200 bg-amber-50 py-2 pl-8 pr-3 text-sm text-amber-900">
                        {warnings.map((warning) => <li key={warning}>{warning}</li>)}
                      </ul>
                    )}

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <label className="text-xs font-medium">Claim number
                        <input type="number" className={fieldClass} value={draft.claim_number ?? ""} onChange={(e) => setDraft({ ...draft, claim_number: e.target.value })} />
                      </label>
                      <label className="text-xs font-medium">Filed date
                        <input type="date" className={fieldClass} value={draft.filed_date || ""} onChange={(e) => setDraft({ ...draft, filed_date: e.target.value })} />
                      </label>
                      <label className="text-xs font-medium">Status
                        <select className={fieldClass} value={draft.status || "active"} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                          <option value="active">Active</option>
                          <option value="transferred">Transferred</option>
                          <option value="withdrawn">Withdrawn</option>
                          <option value="disallowed">Disallowed</option>
                          <option value="duplicate">Duplicate</option>
                          <option value="excluded">Excluded from plan</option>
                        </select>
                      </label>
                      <label className="flex items-end gap-2 pb-2 text-sm">
                        <input type="checkbox" checked={!!draft.include_in_plan} onChange={(e) => setDraft({ ...draft, include_in_plan: e.target.checked })} /> Use in plan calculations
                      </label>

                      <label className="text-xs font-medium md:col-span-2">Creditor / claimant
                        <input className={fieldClass} value={draft.creditor_name || ""} onChange={(e) => setDraft({ ...draft, creditor_name: e.target.value })} />
                      </label>
                      <label className="text-xs font-medium md:col-span-2">Servicer
                        <input className={fieldClass} value={draft.servicer_name || ""} onChange={(e) => setDraft({ ...draft, servicer_name: e.target.value })} />
                      </label>
                      <label className="text-xs font-medium md:col-span-4">Address
                        <textarea className={fieldClass} rows="2" value={draft.creditor_address || ""} onChange={(e) => setDraft({ ...draft, creditor_address: e.target.value })} />
                      </label>

                      <label className="text-xs font-medium">Liability
                        <select className={fieldClass} value={draft.liability || "unknown"} onChange={(e) => setDraft({ ...draft, liability: e.target.value })}>
                          <option value="unknown">Not reviewed</option>
                          <option value="debtor1">{debtorOne} (Debtor 1)</option>
                          {debtors[1] && <option value="debtor2">{debtorTwo} (Debtor 2)</option>}
                          {debtors[1] && <option value="joint">Joint</option>}
                          <option value="debtor1_third">{debtorOne} and third party</option>
                          {debtors[1] && <option value="debtor2_third">{debtorTwo} and third party</option>}
                          <option value="third">Third party only</option>
                        </select>
                      </label>
                      <label className="text-xs font-medium">Classification
                        <select className={fieldClass} value={draft.claim_classification || "unsecured"} onChange={(e) => setDraft({ ...draft, claim_classification: e.target.value })}>
                          <option value="unsecured">General unsecured</option>
                          <option value="secured">Secured</option>
                          <option value="priority">Priority</option>
                          <option value="executory">Lease / executory</option>
                          <option value="administrative">Administrative</option>
                          <option value="mixed">Mixed claim</option>
                        </select>
                      </label>
                      <label className="text-xs font-medium">Claim type
                        <select className={fieldClass} value={draft.claim_type || "other"} onChange={(e) => setDraft({ ...draft, claim_type: e.target.value })}>
                          <option value="other">Other</option>
                          <option value="mortgage">Mortgage</option>
                          <option value="vehicle">Vehicle loan</option>
                          <option value="judgment_lien">Judgment lien</option>
                          <option value="statutory_lien">Statutory lien</option>
                          <option value="tax">Tax</option>
                          <option value="dso">Domestic support obligation</option>
                          <option value="student_loan">Student loan</option>
                          <option value="lease">Lease</option>
                          <option value="household_goods">Household goods / furniture</option>
                        </select>
                      </label>
                      <label className="text-xs font-medium">Matched scheduled creditor
                        <select className={fieldClass} value={draft.matched_creditor || ""} onChange={(e) => selectMatchedCreditor(e.target.value)}>
                          <option value="">Unmatched</option>
                          {rankedCreditors.map((creditor) => (
                            <option key={creditor.id} value={creditor.id}>
                              {creditor.account_name} — {money(creditor.balance)}
                              {draft.amount_claimed !== null && draft.amount_claimed !== undefined && draft.amount_claimed !== "" && creditor.balance !== null && creditor.balance !== undefined && creditor.balance !== ""
                                ? ` — difference ${money(Math.abs(Number(creditor.balance) - Number(draft.amount_claimed)))}`
                                : ""}
                            </option>
                          ))}
                        </select>
                        <span className="mt-1 block font-normal text-gray-500">Closest scheduled balance first.</span>
                      </label>

                      {[
                        ["amount_claimed", "Total claim"],
                        ["secured_amount", "Secured portion"],
                        ["priority_amount", "Priority portion"],
                        ["unsecured_amount", "Unsecured portion"],
                        ["administrative_amount", "Administrative portion"],
                        ["arrearage", "Arrearage"],
                        ["interest_rate", "Interest rate (%)"],
                        ["collateral_value", "Collateral value"],
                      ].map(([field, label]) => (
                        <label key={field} className="text-xs font-medium">{label}
                          <input type="number" step={field === "interest_rate" ? "0.0001" : "0.01"} className={fieldClass} value={draft[field] ?? ""} onChange={(e) => setDraft({ ...draft, [field]: e.target.value })} />
                        </label>
                      ))}

                      <label className="text-xs font-medium md:col-span-2">Collateral description
                        <textarea className={fieldClass} rows="2" value={draft.collateral_description || ""} onChange={(e) => setDraft({ ...draft, collateral_description: e.target.value })} />
                      </label>
                      <label className="text-xs font-medium md:col-span-2">Review notes
                        <textarea className={fieldClass} rows="2" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                      </label>
                    </div>

                    <div className="mt-4 rounded border bg-white p-3">
                      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Link2 className="h-4 w-4" /> Linked collateral</div>
                      {(draft.asset_links || []).length > 0 && (
                        <div className="mb-3 space-y-1">
                          {draft.asset_links.map((link) => (
                            <div key={link.id} className="flex items-center justify-between gap-3 rounded bg-gray-50 px-2 py-1 text-sm">
                              <span>{link.asset_type_label}: {link.asset_label} ({money(link.value)})</span>
                              <button type="button" onClick={() => unlinkAsset(link.id)} className="text-red-600 hover:text-red-800" title="Remove collateral link"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <select className={`${fieldClass} flex-1`} value={selectedAsset} onChange={(e) => setSelectedAsset(e.target.value)}>
                          <option value="">Select an asset…</option>
                          {assets.map((asset) => (
                            <option key={`${asset.asset_type}-${asset.object_id}`} value={`${asset.asset_type}::${asset.object_id}`}>
                              {asset.asset_type_label}: {asset.label} — {money(asset.value)}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={linkAsset} disabled={!selectedAsset || saving} className="rounded border border-indigo-300 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">Link asset</button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button type="button" onClick={saveClaim} disabled={saving} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50">
                        <Save className="h-4 w-4" /> Save claim review
                      </button>
                      {!draft.matched_creditor && (
                        <button type="button" onClick={createScheduledCreditor} disabled={saving} className="rounded border border-indigo-300 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                          Create scheduled creditor from claim
                        </button>
                      )}
                    </div>
                    <ClaimCourtDocuments key={draft.id} caseId={caseId} claim={draft} authHeaders={authHeaders} onChange={updateClaimDocuments} />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
